import { useEffect, useState } from "react";
import { Instagram, Play } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InstagramFeed() {
  const [feed, setFeed] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    fetch(`${API}/instagram/feed?limit=6`)
      .then(async (res) => {
        if (!res.ok) throw new Error("feed unavailable");
        return res.json();
      })
      .then(setFeed)
      .catch(() => setFailed(true));
  }, []);

  return (
    <section id="instagram" data-testid="instagram-section" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <SectionHeading
          chapter="15"
          eyebrow="Instagram"
          title="Acompanhe o Barber Steamer de perto."
          subtitle="Bastidores, demonstrações e novidades direto do nosso perfil oficial."
          testId="instagram-heading"
        />

        <Reveal delay={0.15}>
          <div className="mt-14">
            {failed && (
              <a
                href="https://www.instagram.com/barbersteamer_/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="instagram-fallback-link"
                className="inline-flex items-center gap-3 rounded-full border border-gold/40 bg-gold/[0.07] px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-gold transition-colors duration-300 hover:bg-gold/[0.14]"
              >
                <Instagram className="h-5 w-5" aria-hidden="true" />
                Seguir @barbersteamer_ no Instagram
              </a>
            )}

            {!failed && !feed && (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3" data-testid="instagram-loading">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="aspect-square animate-pulse rounded-2xl border border-white/10 bg-graphite/40" />
                ))}
              </div>
            )}

            {feed && (
              <>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3" data-testid="instagram-grid">
                  {feed.posts.map((post, i) => {
                    const image =
                      post.media_type === "VIDEO"
                        ? post.thumbnail_url || post.media_url
                        : post.media_url;
                    return (
                      <Reveal key={post.id} delay={i * 0.06}>
                        <a
                          href={post.permalink}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`instagram-post-${i}`}
                          className="group relative block overflow-hidden rounded-2xl border border-white/10"
                        >
                          {image ? (
                            <img
                              src={image}
                              alt={post.caption ? post.caption.slice(0, 120) : "Post do Instagram Barber Steamer"}
                              loading="lazy"
                              className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                          ) : (
                            <div className="flex aspect-square items-center justify-center bg-graphite/60 text-sm text-bone/50">
                              Ver no Instagram
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />
                          {post.media_type === "VIDEO" && (
                            <span className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-ink/70 text-gold backdrop-blur-sm">
                              <Play className="ml-0.5 h-3.5 w-3.5" fill="currentColor" aria-hidden="true" />
                            </span>
                          )}
                          {post.caption && (
                            <p className="absolute inset-x-0 bottom-0 line-clamp-2 p-4 text-xs leading-relaxed text-bone/85 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                              {post.caption}
                            </p>
                          )}
                        </a>
                      </Reveal>
                    );
                  })}
                </div>

                <div className="mt-10 flex justify-center">
                  <a
                    href={feed.profile_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="instagram-follow-btn"
                    className="inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-ink shadow-[0_0_40px_rgba(252,172,4,0.25)] transition-[background-color,box-shadow] duration-300 hover:bg-gold-light hover:shadow-[0_0_60px_rgba(252,172,4,0.4)]"
                  >
                    <Instagram className="h-5 w-5" aria-hidden="true" />
                    Seguir @{feed.username}
                  </a>
                </div>
              </>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
